import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout.tsx';
import { apiClient } from '../api/client';
import {
  AlertTriangle,
  Users,
  Search,
  CheckCircle2,
  ExternalLink,
  ShieldAlert,
  RefreshCw,
  Building2,
  UserX,
  Layers,
  ArrowUpRight,
  Filter,
  Trash2,
  CheckSquare,
  Square,
  Loader2,
  Sparkles,
  UserCheck
} from 'lucide-react';

export default function AdminAuditPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'mismatched' | 'multiple'>('mismatched');
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedMultipleIds, setSelectedMultipleIds] = useState<string[]>([]);
  const [toastMessage, setToastMessage] = useState('');

  const { data, isLoading, isRefetching, refetch } = useQuery({
    queryKey: ['adminAuditValidation'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/audit/validation');
      return res.data.data;
    },
    refetchInterval: 30000
  });

  // Tab 1: Clear Mismatches Mutation
  const clearMutation = useMutation({
    mutationFn: async (ideaIds?: string[]) => {
      const res = await apiClient.post('/admin/audit/clear-mismatches', { ideaIds });
      return res.data.data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['adminAuditValidation'] });
      setSelectedIds([]);
      setToastMessage(`Successfully unassigned ${data.unassignedCount} mismatched user(s) across ${data.clearedIdeasCount} idea(s).`);
      setTimeout(() => setToastMessage(''), 4000);
    }
  });

  // Tab 2: Clear / Trim Multiple Assignees Mutation
  const clearMultipleMutation = useMutation({
    mutationFn: async (payload: { mode: 'keep_one' | 'unassign_all'; ideaIds?: string[] }) => {
      const res = await apiClient.post('/admin/audit/clear-multiple-assignees', payload);
      return res.data.data;
    },
    onSuccess: (data: any, variables: any) => {
      queryClient.invalidateQueries({ queryKey: ['adminAuditValidation'] });
      setSelectedMultipleIds([]);
      const modeText = variables.mode === 'keep_one' ? 'retained 1 assignee & removed extra assignees' : 'unassigned all assignees';
      setToastMessage(`Successfully ${modeText} across ${data.processedIdeasCount} idea(s) (removed ${data.removedAssigneesCount} assignees).`);
      setTimeout(() => setToastMessage(''), 4000);
    }
  });

  const mismatchedIdeas = data?.mismatchedIdeas || [];
  const multipleAssigneeIdeas = data?.multipleAssigneeIdeas || [];
  const stats = data?.stats || { totalMismatchedIdeas: 0, totalMultipleAssigneeIdeas: 0 };

  // Collect unique departments for filtering
  const allDepartments = useMemo(() => {
    const set = new Set<string>();
    mismatchedIdeas.forEach((i: any) => i.department && set.add(i.department));
    multipleAssigneeIdeas.forEach((i: any) => i.department && set.add(i.department));
    return Array.from(set).sort();
  }, [mismatchedIdeas, multipleAssigneeIdeas]);

  // Filter Tab 1: Mismatched Ideas
  const filteredMismatchedIdeas = useMemo(() => {
    return mismatchedIdeas.filter((idea: any) => {
      const matchesSearch =
        !searchTerm ||
        idea.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        idea.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        idea.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
        idea.mismatchedAssignees.some((a: any) =>
          a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          a.email.toLowerCase().includes(searchTerm.toLowerCase())
        );

      const matchesDept = !departmentFilter || idea.department === departmentFilter;

      return matchesSearch && matchesDept;
    });
  }, [mismatchedIdeas, searchTerm, departmentFilter]);

  // Filter Tab 2: Multiple Assignee Ideas
  const filteredMultipleAssigneeIdeas = useMemo(() => {
    return multipleAssigneeIdeas.filter((idea: any) => {
      const matchesSearch =
        !searchTerm ||
        idea.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        idea.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        idea.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
        idea.assignees.some((a: any) =>
          a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          a.email.toLowerCase().includes(searchTerm.toLowerCase())
        );

      const matchesDept = !departmentFilter || idea.department === departmentFilter;

      return matchesSearch && matchesDept;
    });
  }, [multipleAssigneeIdeas, searchTerm, departmentFilter]);

  // Tab 1 Selection Logic
  const isAllSelected =
    filteredMismatchedIdeas.length > 0 &&
    filteredMismatchedIdeas.every((i: any) => selectedIds.includes(i.id));

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredMismatchedIdeas.map((i: any) => i.id));
    }
  };

  const toggleSelectRow = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  // Tab 2 Selection Logic
  const isAllMultipleSelected =
    filteredMultipleAssigneeIdeas.length > 0 &&
    filteredMultipleAssigneeIdeas.every((i: any) => selectedMultipleIds.includes(i.id));

  const toggleSelectAllMultiple = () => {
    if (isAllMultipleSelected) {
      setSelectedMultipleIds([]);
    } else {
      setSelectedMultipleIds(filteredMultipleAssigneeIdeas.map((i: any) => i.id));
    }
  };

  const toggleSelectMultipleRow = (id: string) => {
    setSelectedMultipleIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  // Handlers for Tab 1
  const handleClearSelected = () => {
    if (selectedIds.length === 0) return;
    if (window.confirm(`Are you sure you want to unassign mismatched users for ${selectedIds.length} selected idea(s)?`)) {
      clearMutation.mutate(selectedIds);
    }
  };

  const handleClearAll = () => {
    if (mismatchedIdeas.length === 0) return;
    if (window.confirm(`Are you sure you want to unassign ALL mismatched users across ALL ${mismatchedIdeas.length} flagged idea(s)?`)) {
      clearMutation.mutate(undefined);
    }
  };

  // Handlers for Tab 2
  const handleMultipleKeepOne = (ids?: string[]) => {
    const targetCount = ids ? ids.length : multipleAssigneeIdeas.length;
    if (targetCount === 0) return;
    if (window.confirm(`Keep 1 primary assignee and remove extra assignees for ${targetCount} idea(s)?`)) {
      clearMultipleMutation.mutate({ mode: 'keep_one', ideaIds: ids });
    }
  };

  const handleMultipleUnassignAll = (ids?: string[]) => {
    const targetCount = ids ? ids.length : multipleAssigneeIdeas.length;
    if (targetCount === 0) return;
    if (window.confirm(`Unassign ALL assignees for ${targetCount} multiple-assignee idea(s)?`)) {
      clearMultipleMutation.mutate({ mode: 'unassign_all', ideaIds: ids });
    }
  };

  return (
    <Layout title="Idea Audit & Integrity">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Toast Notification */}
        {toastMessage && (
          <div className="bg-emerald-600 text-white p-4 rounded-xl shadow-xl flex items-center justify-between border border-emerald-500 animate-in slide-in-from-top-4">
            <div className="flex items-center space-x-3">
              <CheckCircle2 className="w-5 h-5" />
              <span className="text-xs font-bold">{toastMessage}</span>
            </div>
            <button onClick={() => setToastMessage('')} className="text-white hover:opacity-80 text-xs font-bold">
              Dismiss
            </button>
          </div>
        )}

        {/* Header Title Section */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 p-6 rounded-2xl text-white shadow-xl border border-indigo-900/40">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-xl text-red-400">
              <ShieldAlert className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight font-display">
                Data Audit & Integrity
              </h1>
              <p className="text-xs text-slate-300 mt-1 font-medium">
                Identify department/assignee mismatches and multi-assignee ideas across committees
              </p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            disabled={isRefetching}
            className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-semibold backdrop-blur-md transition-all border border-white/10 self-start sm:self-auto disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isRefetching ? 'animate-spin' : ''}`} />
            <span>Refresh Data</span>
          </button>
        </div>

        {/* Top Summary Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div
            onClick={() => setActiveTab('mismatched')}
            className={`p-5 rounded-2xl border transition-all cursor-pointer shadow-sm ${
              activeTab === 'mismatched'
                ? 'bg-amber-500/10 border-amber-500/40 ring-2 ring-amber-500/20'
                : 'bg-white border-slate-200 hover:border-amber-300 hover:shadow-md'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2.5 bg-amber-100 text-amber-700 rounded-xl">
                  <UserX className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Mismatched Assignees
                  </h3>
                  <p className="text-2xl font-black text-slate-800 mt-0.5">
                    {stats.totalMismatchedIdeas}
                  </p>
                </div>
              </div>
              <span className="text-[10px] font-bold px-2.5 py-1 bg-amber-100 text-amber-800 rounded-full">
                Tab 1
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Ideas assigned to users outside their designated committee/department scope or invalid roles
            </p>
          </div>

          <div
            onClick={() => setActiveTab('multiple')}
            className={`p-5 rounded-2xl border transition-all cursor-pointer shadow-sm ${
              activeTab === 'multiple'
                ? 'bg-indigo-500/10 border-indigo-500/40 ring-2 ring-indigo-500/20'
                : 'bg-white border-slate-200 hover:border-indigo-300 hover:shadow-md'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2.5 bg-indigo-100 text-indigo-700 rounded-xl">
                  <Layers className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Multiple Assignees
                  </h3>
                  <p className="text-2xl font-black text-slate-800 mt-0.5">
                    {stats.totalMultipleAssigneeIdeas}
                  </p>
                </div>
              </div>
              <span className="text-[10px] font-bold px-2.5 py-1 bg-indigo-100 text-indigo-800 rounded-full">
                Tab 2
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Ideas that have 2 or more active committee members assigned
            </p>
          </div>
        </div>

        {/* Tab Selection Navigation */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-200 gap-4 pb-2">
          <div className="flex space-x-2">
            <button
              onClick={() => setActiveTab('mismatched')}
              className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all ${
                activeTab === 'mismatched'
                  ? 'bg-slate-900 text-white shadow-md'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <span>Idea & Assignee Validation</span>
              <span className={`px-2 py-0.5 text-[10px] rounded-full ${activeTab === 'mismatched' ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-700'}`}>
                {filteredMismatchedIdeas.length}
              </span>
            </button>

            <button
              onClick={() => setActiveTab('multiple')}
              className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all ${
                activeTab === 'multiple'
                  ? 'bg-slate-900 text-white shadow-md'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <Users className="w-4 h-4 text-indigo-400" />
              <span>Multiple Assignees</span>
              <span className={`px-2 py-0.5 text-[10px] rounded-full ${activeTab === 'multiple' ? 'bg-indigo-500 text-white' : 'bg-slate-200 text-slate-700'}`}>
                {filteredMultipleAssigneeIdeas.length}
              </span>
            </button>
          </div>

          {/* Search & Department Filter */}
          <div className="flex items-center space-x-3">
            <div className="relative flex-1 sm:w-64">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search ideas or users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-white border border-slate-300 rounded-xl text-xs focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
              />
            </div>

            {allDepartments.length > 0 && (
              <div className="relative">
                <select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  className="pl-8 pr-4 py-2 bg-white border border-slate-300 rounded-xl text-xs font-semibold text-slate-700 focus:ring-2 focus:ring-indigo-500 outline-none transition-all appearance-none"
                >
                  <option value="">All Departments</option>
                  {allDepartments.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </select>
                <Filter className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              </div>
            )}
          </div>
        </div>

        {/* Action Toolbar for Tab 1 */}
        {activeTab === 'mismatched' && filteredMismatchedIdeas.length > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center space-x-3">
              <Sparkles className="w-5 h-5 text-amber-600 shrink-0" />
              <div>
                <h4 className="text-xs font-bold text-amber-900">Unassign Unwanted Mismatched Assignees</h4>
                <p className="text-[11px] text-amber-700 mt-0.5">
                  Select specific ideas or clear all mismatched committee members in bulk.
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2 self-end sm:self-auto">
              <button
                onClick={handleClearSelected}
                disabled={selectedIds.length === 0 || clearMutation.isPending}
                className="flex items-center space-x-1.5 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm disabled:opacity-40"
              >
                {clearMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                <span>Unassign Selected ({selectedIds.length})</span>
              </button>

              <button
                onClick={handleClearAll}
                disabled={clearMutation.isPending}
                className="flex items-center space-x-1.5 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm disabled:opacity-40"
              >
                {clearMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserX className="w-3.5 h-3.5" />}
                <span>Unassign All Mismatches</span>
              </button>
            </div>
          </div>
        )}

        {/* Action Toolbar for Tab 2 */}
        {activeTab === 'multiple' && filteredMultipleAssigneeIdeas.length > 0 && (
          <div className="bg-indigo-500/10 border border-indigo-500/20 p-4 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center space-x-3">
              <Layers className="w-5 h-5 text-indigo-600 shrink-0" />
              <div>
                <h4 className="text-xs font-bold text-indigo-900">Manage Multiple Assignee Ideas</h4>
                <p className="text-[11px] text-indigo-700 mt-0.5">
                  Keep 1 primary assignee (remove extra members) or unassign all assignees.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 self-end sm:self-auto">
              <button
                onClick={() => handleMultipleKeepOne(selectedMultipleIds.length > 0 ? selectedMultipleIds : undefined)}
                disabled={clearMultipleMutation.isPending}
                className="flex items-center space-x-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm disabled:opacity-40"
              >
                {clearMultipleMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserCheck className="w-3.5 h-3.5" />}
                <span>Keep 1 Assignee {selectedMultipleIds.length > 0 ? `(${selectedMultipleIds.length})` : '(All)'}</span>
              </button>

              <button
                onClick={() => handleMultipleUnassignAll(selectedMultipleIds.length > 0 ? selectedMultipleIds : undefined)}
                disabled={clearMultipleMutation.isPending}
                className="flex items-center space-x-1.5 px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-bold transition-all shadow-sm disabled:opacity-40"
              >
                {clearMultipleMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserX className="w-3.5 h-3.5" />}
                <span>Unassign All {selectedMultipleIds.length > 0 ? `(${selectedMultipleIds.length})` : '(All)'}</span>
              </button>
            </div>
          </div>
        )}

        {/* Loading Spinner */}
        {isLoading ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center shadow-sm">
            <RefreshCw className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-3" />
            <p className="text-sm font-semibold text-slate-600">Performing audit validation scan...</p>
          </div>
        ) : activeTab === 'mismatched' ? (
          /* TAB 1: Idea & Assignee Validation Table */
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            {filteredMismatchedIdeas.length === 0 ? (
              <div className="p-12 text-center">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <h3 className="text-base font-bold text-slate-800">No Mismatched Assignees Found</h3>
                <p className="text-xs text-slate-500 mt-1">
                  All assigned ideas correctly match their respective committee and department scopes.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500 font-bold border-b border-slate-200 uppercase tracking-wider text-[10px]">
                      <th className="p-4 w-10 text-center">
                        <button
                          type="button"
                          onClick={toggleSelectAll}
                          className="text-slate-500 hover:text-slate-800 transition-colors"
                        >
                          {isAllSelected ? (
                            <CheckSquare className="w-4 h-4 text-amber-600" />
                          ) : (
                            <Square className="w-4 h-4" />
                          )}
                        </button>
                      </th>
                      <th className="p-4">Idea / Ticket ID</th>
                      <th className="p-4">Idea Department</th>
                      <th className="p-4">Mismatched Assignee</th>
                      <th className="p-4">User's Assigned Committee Scope</th>
                      <th className="p-4 text-center">Status</th>
                      <th className="p-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 font-medium text-slate-700">
                    {filteredMismatchedIdeas.map((idea: any) => {
                      const isSelected = selectedIds.includes(idea.id);
                      return (
                        <tr
                          key={idea.id}
                          className={`transition-colors ${
                            isSelected ? 'bg-amber-100/60 font-semibold' : 'hover:bg-amber-50/40'
                          }`}
                        >
                          <td className="p-4 text-center">
                            <button
                              type="button"
                              onClick={() => toggleSelectRow(idea.id)}
                              className="text-slate-500 hover:text-slate-800 transition-colors"
                            >
                              {isSelected ? (
                                <CheckSquare className="w-4 h-4 text-amber-600" />
                              ) : (
                                <Square className="w-4 h-4" />
                              )}
                            </button>
                          </td>

                          <td className="p-4">
                            <div className="font-bold text-slate-900 text-sm max-w-xs truncate" title={idea.title}>
                              {idea.title}
                            </div>
                            <div className="text-[11px] font-mono text-indigo-600 font-bold mt-0.5">
                              #{idea.id.slice(-8).toUpperCase()}
                            </div>
                            <div className="text-[10px] text-slate-400 mt-0.5">
                              Submitted by: {idea.employee?.name || 'Unknown'}
                            </div>
                          </td>

                          <td className="p-4">
                            <span className="inline-flex items-center space-x-1.5 px-2.5 py-1 bg-slate-100 text-slate-800 rounded-lg font-semibold text-xs border border-slate-200">
                              <Building2 className="w-3.5 h-3.5 text-slate-500" />
                              <span>{idea.department}</span>
                            </span>
                          </td>

                          <td className="p-4">
                            {idea.mismatchedAssignees.map((user: any) => (
                              <div key={user.userId} className="space-y-0.5 my-1">
                                <div className="font-bold text-slate-900 flex items-center space-x-1.5">
                                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                  <span>{user.name}</span>
                                </div>
                                <div className="text-[10px] text-slate-500">{user.email}</div>
                                <div className="text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded w-max">
                                  Role: {user.role.replace(/_/g, ' ')}
                                </div>
                              </div>
                            ))}
                          </td>

                          <td className="p-4">
                            {idea.mismatchedAssignees.map((user: any) => (
                              <div key={user.userId} className="flex flex-wrap gap-1 max-w-xs">
                                {user.managedDepartments.length > 0 ? (
                                  user.managedDepartments.map((dept: string) => (
                                    <span key={dept} className="px-2 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded text-[10px] font-semibold">
                                      {dept}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-slate-400 italic text-[11px]">No committees assigned</span>
                                )}
                              </div>
                            ))}
                          </td>

                          <td className="p-4 text-center space-y-1">
                            {idea.mismatchedAssignees.map((user: any) => (
                              <div key={user.userId} className="flex flex-col items-center space-y-1">
                                {(user.mismatchReasons || ['Department Scope Mismatch']).map((reason: string, idx: number) => (
                                  <span key={idx} className="inline-flex items-center space-x-1 px-2.5 py-0.5 bg-red-100 text-red-800 rounded-full font-bold text-[10px] border border-red-200">
                                    <AlertTriangle className="w-3 h-3 text-red-600 shrink-0" />
                                    <span>{reason}</span>
                                  </span>
                                ))}
                              </div>
                            ))}
                          </td>

                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                type="button"
                                onClick={() => clearMutation.mutate([idea.id])}
                                disabled={clearMutation.isPending}
                                className="px-2.5 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-900 rounded-lg font-bold text-xs transition-colors flex items-center space-x-1"
                              >
                                <UserX className="w-3 h-3 text-amber-700" />
                                <span>Unassign</span>
                              </button>

                              <Link
                                to={`/ideas/edit/${idea.id}`}
                                className="inline-flex items-center space-x-1 px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-bold text-xs transition-colors shadow-sm"
                              >
                                <span>Manage</span>
                                <ArrowUpRight className="w-3.5 h-3.5" />
                              </Link>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          /* TAB 2: Multiple Assignees Table */
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            {filteredMultipleAssigneeIdeas.length === 0 ? (
              <div className="p-12 text-center">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <h3 className="text-base font-bold text-slate-800">No Multiple Assignee Ideas Found</h3>
                <p className="text-xs text-slate-500 mt-1">
                  All ideas currently have single or zero assignees.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500 font-bold border-b border-slate-200 uppercase tracking-wider text-[10px]">
                      <th className="p-4 w-10 text-center">
                        <button
                          type="button"
                          onClick={toggleSelectAllMultiple}
                          className="text-slate-500 hover:text-slate-800 transition-colors"
                        >
                          {isAllMultipleSelected ? (
                            <CheckSquare className="w-4 h-4 text-indigo-600" />
                          ) : (
                            <Square className="w-4 h-4" />
                          )}
                        </button>
                      </th>
                      <th className="p-4">Idea / Ticket ID</th>
                      <th className="p-4">Idea Department</th>
                      <th className="p-4">All Assigned Users</th>
                      <th className="p-4 text-center">Assignee Count</th>
                      <th className="p-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 font-medium text-slate-700">
                    {filteredMultipleAssigneeIdeas.map((idea: any) => {
                      const isSelected = selectedMultipleIds.includes(idea.id);
                      return (
                        <tr key={idea.id} className={`transition-colors ${isSelected ? 'bg-indigo-100/60 font-semibold' : 'hover:bg-indigo-50/40'}`}>
                          <td className="p-4 text-center">
                            <button
                              type="button"
                              onClick={() => toggleSelectMultipleRow(idea.id)}
                              className="text-slate-500 hover:text-slate-800 transition-colors"
                            >
                              {isSelected ? (
                                <CheckSquare className="w-4 h-4 text-indigo-600" />
                              ) : (
                                <Square className="w-4 h-4" />
                              )}
                            </button>
                          </td>

                          <td className="p-4">
                            <div className="font-bold text-slate-900 text-sm max-w-xs truncate" title={idea.title}>
                              {idea.title}
                            </div>
                            <div className="text-[11px] font-mono text-indigo-600 font-bold mt-0.5">
                              #{idea.id.slice(-8).toUpperCase()}
                            </div>
                            <div className="text-[10px] text-slate-400 mt-0.5">
                              Submitted by: {idea.employee?.name || 'Unknown'}
                            </div>
                          </td>

                          <td className="p-4">
                            <span className="inline-flex items-center space-x-1.5 px-2.5 py-1 bg-slate-100 text-slate-800 rounded-lg font-semibold text-xs border border-slate-200">
                              <Building2 className="w-3.5 h-3.5 text-slate-500" />
                              <span>{idea.department}</span>
                            </span>
                          </td>

                          <td className="p-4">
                            <div className="space-y-1.5">
                              {idea.assignees.map((user: any, index: number) => (
                                <div key={user.id || index} className="flex items-center space-x-2">
                                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-[10px] shrink-0 border border-indigo-200">
                                    {user.name?.charAt(0) || 'U'}
                                  </div>
                                  <div>
                                    <div className="font-bold text-slate-800 text-xs">{user.name}</div>
                                    <div className="text-[10px] text-slate-500">{user.email}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </td>

                          <td className="p-4 text-center">
                            <span className="inline-flex items-center space-x-1 px-3 py-1 bg-indigo-100 text-indigo-900 rounded-full font-black text-xs border border-indigo-200">
                              <Users className="w-3.5 h-3.5 text-indigo-600" />
                              <span>{idea.assigneesCount} Assignees</span>
                            </span>
                          </td>

                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                type="button"
                                onClick={() => handleMultipleKeepOne([idea.id])}
                                disabled={clearMultipleMutation.isPending}
                                className="px-2.5 py-1.5 bg-indigo-100 hover:bg-indigo-200 text-indigo-900 rounded-lg font-bold text-xs transition-colors flex items-center space-x-1"
                                title="Keep 1 primary assignee and remove extra assignees"
                              >
                                <UserCheck className="w-3 h-3 text-indigo-700" />
                                <span>Keep 1</span>
                              </button>

                              <button
                                type="button"
                                onClick={() => handleMultipleUnassignAll([idea.id])}
                                disabled={clearMultipleMutation.isPending}
                                className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg font-bold text-xs transition-colors flex items-center space-x-1"
                                title="Unassign all members from this idea"
                              >
                                <UserX className="w-3 h-3 text-slate-600" />
                                <span>Unassign All</span>
                              </button>

                              <Link
                                to={`/ideas/edit/${idea.id}`}
                                className="inline-flex items-center space-x-1 px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-bold text-xs transition-colors shadow-sm"
                              >
                                <span>View / Edit</span>
                                <ExternalLink className="w-3.5 h-3.5" />
                              </Link>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
